import { useCallback, useMemo, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { RelativeTime } from '@/components/DataDisplay'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { fetchApps, deleteApp, APP_MOD_STR as S, type AppRow } from './appModApi'

/**
 * A5.25 — Ilovani o'chirish (hard-delete). GET /admin/mini-apps (lane-A greenfield →
 * fail-soft bo'sh). O'chirish → DELETE /admin/mini-apps/:id. DangerDialog L3:
 * sabab + nom terish shart (qaytarilmaydigan amal).
 */
export function AppDeleteScreen() {
  const { toast } = useToast()
  const [target, setTarget] = useState<AppRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<AppRow>[]>(
    () => [
      {
        key: 'title',
        header: S.app,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title}</span>
            <code className="text-xs text-accent">/{r.short_name}</code>
          </div>
        ),
      },
      {
        key: 'bot_username',
        header: S.bot,
        type: 'text',
        width: 160,
        render: (r) => (r.bot_username ? `@${r.bot_username}` : '—'),
      },
      {
        key: 'status',
        header: S.status,
        type: 'text',
        width: 140,
        render: (r) =>
          r.status === 'suspended' ? (
            <StatusBadge status="suspended" label={S.suspended} />
          ) : (
            <StatusBadge status="active" label={S.active} />
          ),
      },
      { key: 'installs', header: S.installs, type: 'number', width: 110, align: 'right' },
      {
        key: 'created_at',
        header: S.created,
        type: 'date',
        width: 150,
        render: (r) => <RelativeTime value={r.created_at} className="text-secondary" />,
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AppRow>[]>(
    () => [
      { id: 'delete', label: S.deleteApp, icon: <Trash2 size={13} />, danger: true, onSelect: (r) => setTarget(r) },
    ],
    [],
  )

  const fetcher = useCallback(fetchApps, [])

  return (
    <>
      <PageHeader title={S.deleteAppTitle} screenId="A5.25" />
      <DataTable<AppRow>
        key={reloadKey}
        screenId="A5.25"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Trash2 size={28} strokeWidth={1.5} />, title: S.appsEmpty }}
      />

      {target && (
        <DangerDialog
          open
          onClose={() => setTarget(null)}
          screenId="A5.25"
          level={3}
          title={S.deleteAppConfirmTitle}
          description={S.deleteAppConfirmDesc}
          confirmLabel={S.deleteApp}
          objectName={target.short_name ? `/${target.short_name}` : `#${target.id}`}
          onConfirm={async () => {
            try {
              await deleteApp(target.id)
              toast(S.appDeleted, { tone: 'success' })
              setTarget(null)
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
              throw e
            }
          }}
        />
      )}
    </>
  )
}
